import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { BibleIndex } from '../types/bible';
import { BibleApiService } from '../services/bibleApi';
import { TTSCacheService } from '../services/ttsCache';
import { ReadStatusCacheService } from '../services/readStatusCache';
import ChapterList from './ChapterList';
import VerseList from './VerseList';

type ViewState = 'books' | 'chapters' | 'verses';

interface BibleReaderProps {
  onGoHome?: () => void;
}

export interface BibleReaderRef {
  handleBackToBooks: () => void;
}

const BibleReader = forwardRef<BibleReaderRef, BibleReaderProps>(({ onGoHome }, ref) => {
  const [currentView, setCurrentView] = useState<ViewState>('books');
  const [selectedBook, setSelectedBook] = useState<BibleIndex | null>(null);
  const [selectedChapter, setSelectedChapter] = useState<number | null>(null);
  const [bibleIndices, setBibleIndices] = useState<BibleIndex[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [cacheSize, setCacheSize] = useState<number>(0);
  const [isDeletingCache, setIsDeletingCache] = useState<boolean>(false);
  const [readStatusCount, setReadStatusCount] = useState<number>(0);
  const [isDeletingReadStatus, setIsDeletingReadStatus] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [showTtsTooltip, setShowTtsTooltip] = useState<boolean>(false);
  const [showReadTooltip, setShowReadTooltip] = useState<boolean>(false);

  useImperativeHandle(ref, () => ({
    handleBackToBooks
  }));

  useEffect(() => {
    const fetchBibleIndices = async () => {
      try {
        setLoading(true);
        setError(null);
        const indices = await BibleApiService.getBibleIndices();
        setBibleIndices(indices);
        
        // 캐시 크기 조회
        const size = await TTSCacheService.getCacheSize();
        setCacheSize(size);
        
        // 읽음 상태 개수 조회
        const readCount = await ReadStatusCacheService.getReadStatusCount();
        setReadStatusCount(readCount);
      } catch (err) {
        setError(err instanceof Error ? err.message : '알 수 없는 오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchBibleIndices();
  }, []);

  // 화면 크기 감지
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => {
      window.removeEventListener('resize', checkIsMobile);
    };
  }, []);

  const handleBookSelect = (book: BibleIndex) => {
    setSelectedBook(book);
    setCurrentView('chapters');
    // 모바일에서 스크롤 후 렌더링 문제 해결을 위한 강제 리렌더링
    if (isMobile) {
      setTimeout(() => {
        window.scrollTo(0, 0);
        // 뷰포트 강제 업데이트
        const viewport = document.querySelector('meta[name="viewport"]');
        if (viewport) {
          viewport.setAttribute('content', 'width=device-width, initial-scale=1.0');
        }
      }, 100);
    }
  };

  const handleChapterSelect = (book: BibleIndex, chapter: number) => {
    setSelectedBook(book);
    setSelectedChapter(chapter);
    setCurrentView('verses');
    // 모바일에서 스크롤 후 렌더링 문제 해결을 위한 강제 리렌더링
    if (isMobile) {
      setTimeout(() => {
        window.scrollTo(0, 0);
        // 뷰포트 강제 업데이트
        const viewport = document.querySelector('meta[name="viewport"]');
        if (viewport) {
          viewport.setAttribute('content', 'width=device-width, initial-scale=1.0');
        }
      }, 100);
    }
  };

  const handleBackToBooks = () => {
    setCurrentView('books');
    setSelectedBook(null);
    setSelectedChapter(null);
    // 모바일에서 스크롤 후 렌더링 문제 해결을 위한 강제 리렌더링
    if (isMobile) {
      setTimeout(() => {
        window.scrollTo(0, 0);
        // 뷰포트 강제 업데이트
        const viewport = document.querySelector('meta[name="viewport"]');
        if (viewport) {
          viewport.setAttribute('content', 'width=device-width, initial-scale=1.0');
        }
      }, 100);
    }
    if (onGoHome) {
      onGoHome();
    }
  };

  const handleBackToChapters = () => {
    setCurrentView('chapters');
    setSelectedChapter(null);
  };

  const handleClearCache = async () => {
    if (window.confirm('모든 음성 캐시를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) {
      try {
        setIsDeletingCache(true);
        await TTSCacheService.clearAllCache();
        setCacheSize(0);
        alert('음성 캐시가 모두 삭제되었습니다.');
      } catch (error) {
        console.error('캐시 삭제 중 오류:', error);
        alert('캐시 삭제 중 오류가 발생했습니다.');
      } finally {
        setIsDeletingCache(false);
      }
    }
  };

  const handleClearReadStatus = async () => {
    if (window.confirm('모든 읽음 상태를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) {
      try {
        setIsDeletingReadStatus(true);
        await ReadStatusCacheService.clearAllReadStatus();
        setReadStatusCount(0);
        alert('읽음 상태가 모두 삭제되었습니다.');
      } catch (error) {
        console.error('읽음 상태 삭제 중 오류:', error);
        alert('읽음 상태 삭제 중 오류가 발생했습니다.');
      } finally {
        setIsDeletingReadStatus(false);
      }
    }
  };

  const handlePreviousChapter = () => {
    if (!selectedBook || !selectedChapter) return;
    
    if (selectedChapter > 1) {
      // 같은 성경의 이전 장
      setSelectedChapter(selectedChapter - 1);
    } else {
      // 이전 성경의 마지막 장
      const currentBookIndex = bibleIndices.findIndex(book => book.bookNo === selectedBook.bookNo);
      if (currentBookIndex > 0) {
        const previousBook = bibleIndices[currentBookIndex - 1];
        setSelectedBook(previousBook);
        setSelectedChapter(previousBook.chapters);
      }
    }
  };

  const handleNextChapter = () => {
    if (!selectedBook || !selectedChapter) return;
    
    if (selectedChapter < selectedBook.chapters) {
      // 같은 성경의 다음 장
      setSelectedChapter(selectedChapter + 1);
    } else {
      // 다음 성경의 첫 장
      const currentBookIndex = bibleIndices.findIndex(book => book.bookNo === selectedBook.bookNo);
      if (currentBookIndex < bibleIndices.length - 1) {
        const nextBook = bibleIndices[currentBookIndex + 1];
        setSelectedBook(nextBook);
        setSelectedChapter(1);
      }
    }
  };

  if (currentView === 'chapters' && selectedBook) {
    return (
      <ChapterList 
        book={selectedBook} 
        onChapterSelect={handleChapterSelect} 
        onBack={handleBackToBooks} 
      />
    );
  }

  if (currentView === 'verses' && selectedBook && selectedChapter) {
    return (
      <VerseList 
        book={selectedBook} 
        chapter={selectedChapter} 
        onBack={handleBackToChapters}
        onGoHome={handleBackToBooks}
        onPreviousChapter={handlePreviousChapter}
        onNextChapter={handleNextChapter}
      />
    );
  }

  // 성경 목록 표시
  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p>성경 인덱스를 불러오는 중...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: 'red' }}>
        <p>오류가 발생했습니다: {error}</p>
        <button 
          onClick={() => window.location.reload()} 
          style={{ marginTop: '10px', padding: '8px 16px' }}
        >
          다시 시도
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: isMobile 
          ? 'repeat(auto-fill, minmax(80px, 1fr))' 
          : 'repeat(auto-fill, minmax(200px, 1fr))', 
        gap: isMobile ? '8px' : '15px' 
      }}>
        {bibleIndices.map((book) => (
          <div 
            key={book.bookNo} 
            onClick={() => handleBookSelect(book)}
            style={{ 
              border: '1px solid #ddd', 
              borderRadius: isMobile ? '6px' : '8px', 
              padding: isMobile ? '8px 4px' : '15px',
              backgroundColor: book.bookNo <= 39 ? '#f8f9fa' : '#e8f4fd',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              minHeight: isMobile ? '60px' : 'auto',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = book.bookNo <= 39 ? '#e9ecef' : '#d1ecf1';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = book.bookNo <= 39 ? '#f8f9fa' : '#e8f4fd';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <h3 style={{ 
              margin: '0', 
              color: book.bookNo <= 39 ? '#8b4513' : '#1e40af', 
              fontSize: isMobile ? '14px' : '18px',
              fontWeight: 'bold'
            }}>
              {isMobile ? (book.acronym || book.name) : book.name}
            </h3>
            {isMobile && book.acronymEN && (
              <p style={{ 
                margin: '2px 0 0 0', 
                fontSize: '10px', 
                color: '#6c757d',
                fontWeight: 'normal'
              }}>
                ({book.acronymEN})
              </p>
            )}
            {!isMobile && (
              <p style={{ margin: '5px 0 0 0', fontSize: '14px', color: '#6c757d' }}>
                ({book.code})
              </p>
            )}
          </div>
        ))}
      </div>
      
      {/* 캐시 관리 */}
      <div style={{ 
        marginTop: '20px', 
        padding: '15px', 
        backgroundColor: '#f8f9fa', 
        borderRadius: '8px',
        border: '1px solid #e9ecef',
        textAlign: 'center'
      }}>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', flexWrap: 'wrap' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', marginBottom: '10px' }}>
              <p style={{ margin: '0', fontSize: '14px', color: '#6c757d' }}>
                음성 캐시: {cacheSize}개 파일
              </p>
              <div style={{ position: 'relative' }}>
                <span 
                  style={{ 
                    cursor: 'pointer', 
                    color: '#007bff', 
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}
                  onMouseEnter={() => setShowTtsTooltip(true)}
                  onMouseLeave={() => setShowTtsTooltip(false)}
                >
                  ?
                </span>
                {showTtsTooltip && (
                  <div style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: '#333',
                    color: 'white',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    zIndex: 1000,
                    marginBottom: '5px',
                    width: '300px',
                    whiteSpace: 'normal',
                    textAlign: 'center'
                  }}>
                    음성캐시는 한번 읽은 절에 대해 빠르게 음성을 재생하기 위해 필요합니다. 특별한 이유가 없으면 삭제하지 않으셔도 됩니다.
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={handleClearCache}
              disabled={isDeletingCache || cacheSize === 0}
              style={{
                padding: '8px 16px',
                backgroundColor: isDeletingCache || cacheSize === 0 ? '#e9ecef' : '#dc3545',
                color: isDeletingCache || cacheSize === 0 ? '#6c757d' : 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: isDeletingCache || cacheSize === 0 ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: 'bold'
              }}
            >
              {isDeletingCache ? '삭제 중...' : '음성 캐시 삭제'}
            </button>
          </div>
          
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', marginBottom: '10px' }}>
              <p style={{ margin: '0', fontSize: '14px', color: '#6c757d' }}>
                읽음 상태: {readStatusCount}개 절
              </p>
              <div style={{ position: 'relative' }}>
                <span 
                  style={{ 
                    cursor: 'pointer', 
                    color: '#007bff', 
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}
                  onMouseEnter={() => setShowReadTooltip(true)}
                  onMouseLeave={() => setShowReadTooltip(false)}
                >
                  ?
                </span>
                {showReadTooltip && (
                  <div style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: '#333',
                    color: 'white',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    zIndex: 1000,
                    marginBottom: '5px',
                    width: '250px',
                    whiteSpace: 'normal',
                    textAlign: 'center'
                  }}>
                    절의 읽음 상태를 초기화 하시려면 읽음캐시삭제 버튼을 눌러주세요.
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={handleClearReadStatus}
              disabled={isDeletingReadStatus || readStatusCount === 0}
              style={{
                padding: '8px 16px',
                backgroundColor: isDeletingReadStatus || readStatusCount === 0 ? '#e9ecef' : '#6f42c1',
                color: isDeletingReadStatus || readStatusCount === 0 ? '#6c757d' : 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: isDeletingReadStatus || readStatusCount === 0 ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: 'bold'
              }}
            >
              {isDeletingReadStatus ? '삭제 중...' : '읽음 캐시 삭제'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

BibleReader.displayName = 'BibleReader';

export default BibleReader;
